import { MethodOfWork, PermitCategory } from "../../common/enums";
import { MeasuresFormState_fromFormState } from "../forms/Permits/Conditions/MeasuresFormState";
import { ProtectiveEquipmentFormState_fromFormState } from "../forms/Permits/Conditions/ProtectiveEquipmentFormState";
import { RiskIdentificationsFormState_fromFormState } from "../forms/Permits/Conditions/RiskIdentificationsFormState";
import GeneralDepartmentPermitFormState from "../forms/Permits/GeneralDepartmentPermitFormState";
import { PositionPickerRow_toPosition } from "../forms/Permits/PositionPickerRow";
import Measures from "../permits/conditions/Measures";
import ProtectiveEquipment from "../permits/conditions/ProtectiveEquipment";
import { RiskIdentifications } from "../permits/conditions/RiskIdentifications";
import Person, { fromParticipantListItem } from "../Person";
import Position from "../Position";

type GeneralDepartmentPermitRequest = {
    departmentId?: number;
    positions: Position[];
    startDate: Date;
    endDate: Date;
    remindMe: boolean;
    expirationReminderEmail: string;
    expirationReminderPhoneNumber: string;
    remindMeDate: Date;
    permitCategory: PermitCategory;
    entrepreneur: string;
    description: string;
    workLeaderName: string;
    workLeaderIsActive: boolean;
    workLeaderPhoneNumber: string;
    persons: Person[];
    projectLeaderName: string;
    projectLeaderPhoneNumber: string;
    projectLeaderIsActive: boolean;
    isWorkFlammable: boolean;
    methodOfWork: MethodOfWork[];
    otherMethodOfWork: string;
    isAuthorizedForHotWork: boolean;
    surveillanceByFireWatch: boolean;
    fireWatchName: string;
    fireWatchNotNeeded: boolean;
    postWorkSurveillanceArranged: boolean;
    postWorkSurveillanceFireWatch: string;
    postWorkSurveillanceHours: number;
    forWorkWithFlammableGoods: boolean;
    workplaceCleaned: boolean;
    combustibleMaterialsRemoved: boolean;
    inAreaWithExplosiveAtmosphere: boolean;
    openings: boolean;
    extinguishingEquipment: boolean;
    weldingEquipment: boolean;
    weldingEquipmentFaultless: boolean;
    emergencyServicesCanBeCalled: boolean;
    waterproofing: boolean;
    openFlamesForMeltingIce: boolean;
    maximumHeat: boolean;
    whenMeltingAsphalt: boolean;
    followingSections: string;
    permitAgreementStartDate?: Date;
    permitAgreementEndDate?: Date;
    permitAgreementFireWatch?: string;
    permitAgreementWayOfCommunication?: string;
    measures: Measures;
    protectiveEquipment: ProtectiveEquipment;
    riskIdentifications: RiskIdentifications;
};

export default GeneralDepartmentPermitRequest;

export function toUpdateRequest(formState: GeneralDepartmentPermitFormState): GeneralDepartmentPermitRequest {
    const request: GeneralDepartmentPermitRequest = {
        positions: formState.positions.map(PositionPickerRow_toPosition),
        startDate: formState.startDate.value,
        endDate: formState.endDate.value,
        remindMe: formState.remindMe.value,
        expirationReminderEmail: formState.expirationReminderEmail.value,
        expirationReminderPhoneNumber: formState.expirationReminderPhoneNumber.value,
        remindMeDate: formState.remindMeDate.value,
        permitCategory: formState.permitCategory.value,
        entrepreneur: formState.entrepreneur.value,
        description: formState.description.value,
        workLeaderName: formState.workLeaderName.value,
        workLeaderIsActive: formState.workLeaderIsActive.value,
        workLeaderPhoneNumber: formState.workLeaderPhoneNumber.value,
        persons: formState.persons.map(fromParticipantListItem),
        projectLeaderName: formState.projectLeaderName.value,
        projectLeaderPhoneNumber: formState.projectLeaderPhoneNumber.value,
        projectLeaderIsActive: formState.projectLeaderIsActive.value,
        isWorkFlammable: formState.isWorkFlammable.value,
        methodOfWork: formState.methodOfWork.value,
        otherMethodOfWork: formState.otherMethodOfWork.value,
        isAuthorizedForHotWork: formState.isAuthorizedForHotWork.value,
        surveillanceByFireWatch: formState.surveillanceByFireWatch.value,
        fireWatchName: formState.fireWatchName.value,
        fireWatchNotNeeded: formState.fireWatchNotNeeded.value,
        postWorkSurveillanceArranged: formState.postWorkSurveillanceArranged.value,
        postWorkSurveillanceFireWatch: formState.postWorkSurveillanceFireWatch.value,
        postWorkSurveillanceHours: formState.postWorkSurveillanceHours.value,
        forWorkWithFlammableGoods: formState.forWorkWithFlammableGoods.value,
        workplaceCleaned: formState.workplaceCleaned.value,
        combustibleMaterialsRemoved: formState.combustibleMaterialsRemoved.value,
        inAreaWithExplosiveAtmosphere: formState.inAreaWithExplosiveAtmosphere.value,
        openings: formState.openings.value,
        extinguishingEquipment: formState.extinguishingEquipment.value,
        weldingEquipment: formState.weldingEquipment.value,
        weldingEquipmentFaultless: formState.weldingEquipmentFaultless.value,
        emergencyServicesCanBeCalled: formState.emergencyServicesCanBeCalled.value,
        waterproofing: formState.waterproofing.value,
        openFlamesForMeltingIce: formState.openFlamesForMeltingIce.value,
        maximumHeat: formState.maximumHeat.value,
        whenMeltingAsphalt: formState.whenMeltingAsphalt.value,
        followingSections: formState.followingSections.value,
        permitAgreementStartDate: formState.permitAgreementStartDate.value,
        permitAgreementEndDate: formState.permitAgreementEndDate.value,
        permitAgreementFireWatch: formState.permitAgreementFireWatch.value,
        permitAgreementWayOfCommunication: formState.permitAgreementWayOfCommunication.value,
        measures: MeasuresFormState_fromFormState(formState.measures),
        protectiveEquipment: ProtectiveEquipmentFormState_fromFormState(formState.protectiveEquipment),
        riskIdentifications: RiskIdentificationsFormState_fromFormState(formState.riskIdentifications)
    };

    return request;
}

export function toCreateRequest(
    formState: GeneralDepartmentPermitFormState,
    departmentId: number
): GeneralDepartmentPermitRequest {
    const request: GeneralDepartmentPermitRequest = {
        departmentId: departmentId,
        positions: formState.positions.map(PositionPickerRow_toPosition),
        startDate: formState.startDate.value,
        endDate: formState.endDate.value,
        remindMe: formState.remindMe.value,
        expirationReminderEmail: formState.expirationReminderEmail.value,
        expirationReminderPhoneNumber: formState.expirationReminderPhoneNumber.value,
        remindMeDate: formState.remindMeDate.value,
        permitCategory: formState.permitCategory.value,
        entrepreneur: formState.entrepreneur.value,
        description: formState.description.value,
        workLeaderName: formState.workLeaderName.value,
        workLeaderIsActive: formState.workLeaderIsActive.value,
        workLeaderPhoneNumber: formState.workLeaderPhoneNumber.value,
        persons: formState.persons.map(fromParticipantListItem),
        projectLeaderName: formState.projectLeaderName.value,
        projectLeaderPhoneNumber: formState.projectLeaderPhoneNumber.value,
        projectLeaderIsActive: formState.projectLeaderIsActive.value,
        isWorkFlammable: formState.isWorkFlammable.value,
        methodOfWork: formState.methodOfWork.value,
        otherMethodOfWork: formState.otherMethodOfWork.value,
        isAuthorizedForHotWork: formState.isAuthorizedForHotWork.value,
        surveillanceByFireWatch: formState.surveillanceByFireWatch.value,
        fireWatchName: formState.fireWatchName.value,
        fireWatchNotNeeded: formState.fireWatchNotNeeded.value,
        postWorkSurveillanceArranged: formState.postWorkSurveillanceArranged.value,
        postWorkSurveillanceFireWatch: formState.postWorkSurveillanceFireWatch.value,
        postWorkSurveillanceHours: formState.postWorkSurveillanceHours.value,
        forWorkWithFlammableGoods: formState.forWorkWithFlammableGoods.value,
        workplaceCleaned: formState.workplaceCleaned.value,
        combustibleMaterialsRemoved: formState.combustibleMaterialsRemoved.value,
        inAreaWithExplosiveAtmosphere: formState.inAreaWithExplosiveAtmosphere.value,
        openings: formState.openings.value,
        extinguishingEquipment: formState.extinguishingEquipment.value,
        weldingEquipment: formState.weldingEquipment.value,
        weldingEquipmentFaultless: formState.weldingEquipmentFaultless.value,
        emergencyServicesCanBeCalled: formState.emergencyServicesCanBeCalled.value,
        waterproofing: formState.waterproofing.value,
        openFlamesForMeltingIce: formState.openFlamesForMeltingIce.value,
        maximumHeat: formState.maximumHeat.value,
        whenMeltingAsphalt: formState.whenMeltingAsphalt.value,
        followingSections: formState.followingSections.value,
        measures: MeasuresFormState_fromFormState(formState.measures),
        protectiveEquipment: ProtectiveEquipmentFormState_fromFormState(formState.protectiveEquipment),
        riskIdentifications: RiskIdentificationsFormState_fromFormState(formState.riskIdentifications)
    };

    return request;
}
